/**
 * Markdown.js
 * -----------------------------------------------------------------------------
 * Renders assistant messages as rich Markdown with:
 *   - GitHub flavoured markdown (tables, task lists, strikethrough) via remark-gfm
 *   - Syntax highlighted code blocks via rehype-highlight (highlight.js)
 *   - A "Copy" button on every fenced code block
 *   - Safe external links (target=_blank + rel=noopener)
 *
 * Kept separate so it can be reused (e.g. in a future preview) and unit tested.
 */

import React, { useState, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';

/**
 * A fenced code block with a copy-to-clipboard button and language label.
 */
function CodeBlock({ inline, className, children, ...props }) {
  const [copied, setCopied] = useState(false);
  const text = String(children).replace(/\n$/, '');

  // Inline code (`like this`) is rendered as a simple styled span.
  if (inline) {
    return (
      <code className="ai-inline-code" {...props}>
        {children}
      </code>
    );
  }

  const langMatch = /language-(\w+)/.exec(className || '');
  const language = langMatch ? langMatch[1] : 'text';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be unavailable */
    }
  };

  return (
    <div className="ai-code-wrapper">
      <div className="ai-code-toolbar">
        <span className="ai-code-lang">{language}</span>
        <button type="button" className="ai-code-copy" onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="ai-code-pre">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
}

const components = {
  code: CodeBlock,
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" className="ai-link">
      {children}
    </a>
  ),
  table: ({ node, ...props }) => (
    <div className="ai-table-wrapper">
      <table {...props} />
    </div>
  ),
};

function Markdown({ content }) {
  return (
    <div className="ai-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeHighlight, { detect: true, ignoreMissing: true }]]}
        components={components}
      >
        {content || ''}
      </ReactMarkdown>
    </div>
  );
}

export default memo(Markdown);
